import React, { FC, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import Button, { ButtonModeE } from "../../components/Button/Button";
import Form from "../../components/Form/Form";
import { H1, H2, H4, Span } from "../../components/Text/Text";
import { FormFieldT } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  addEmptyVersion,
  clearAll,
  setCampaignDetails,
  setDeliveryData,
  setDeliveryDescription,
  setDeliveryWizardStep,
  setEditableVersionIndex,
  setIsDirty,
  setIsShortEdit,
  setPostProductionCompanyId,
  setProductionCompanyId,
  setProductionLocation,
  setSelectedProductionCompany,
  updateDeliveryData,
} from "../../store/slices/deliveryWizardSlice";
import DeliveryVersionRow from "./DeliveryVersionRow";
import { WizardModeE, WizardTypeE } from "./models";
import WizardHeader from "./WizardHeader";
import Tooltip from "../../components/Tooltip/Tooltip";
import AsyncImage from "../../components/AsyncImage/AsyncImage";
import { useDestinations } from "./helpers/useDestinations";
import {
  AccessLevelE,
  IconColorE,
  IconSizeE,
  LangE,
  StatusE,
  TooltipPlaceE,
  UserRoleE,
} from "../../models/enums";
import InfoTooltip from "../../components/InfoTooltip/InfoTooltip";
import { useGetProjectByIdQuery } from "../../api/projectsApi";
import Icon from "../../components/Icon/Icon";
import {
  selectDeliveryWizardData,
  selectSelectedPostProductionCompanyId,
  selectSelectedProductionCompany,
} from "../../store/selectors/deliveryWizardSelector";
import usePopup from "../../utils/hooks/usePopup";
import ChangeCampaignNumberBody from "../ChangeCampaignNumber/ChangeCampaignNumberBody";
import ChangeCampaignNumberFooter from "../ChangeCampaignNumber/ChangeCampaignNumberFooter";
import UnsavedChangesWarning from "../UnsavedChangesWarning";
import { ValidatorsE } from "../../components/Form/yupValidations";
import { selectValue } from "../../utils/helpers/selectOptions";
import { useGetAllClientsQuery } from "../../api/clientAPI";
import { selectCurrentUser } from "../../store/selectors/authSelectors";
import { DATE_FORMAT, DEFAULT_VERSION_DATA } from "../../utils/constants";
import { getDeliveryNameFromCampaignName } from "../../utils/helpers/getDeliveryNameFromCampaignName";
import { isMobileOrTablet } from "../../utils/helpers/mediaQueries";
import { selectCurrentLang } from "../../store/selectors/appSelectors";
import { useTranslate } from "../../utils/hooks/useTranslate";
import getImageLink from "../../utils/helpers/getImageLink";
import CreateNewProductionCompanyShort from "../CreateNewProductionCompanyShort";
import { PopupT } from "../../store/slices/popupSlice";
import { useSelectOptions } from "./helpers/useSelectOptions";

import styles from "./styles.module.scss";

type DeliveryWizardPropTypes = {
  mode: WizardModeE;
  type: WizardTypeE;
  deliveryId?: number;
  campaignDeliveryId?: number;
};

const DeliveryWizard: FC<DeliveryWizardPropTypes> = (props) => {
  const user = useAppSelector(selectCurrentUser);
  const lang = useAppSelector(selectCurrentLang);
  const { t } = useTranslate();
  const { mode, type, deliveryId, campaignDeliveryId } = props;
  const { productionCompaniesSO, filteredPostProductionCompaniesSO } =
    useSelectOptions();
  const { data: defaultDeliveryData, isFetching: isFetchingDelivery } =
    useGetProjectByIdQuery(
      { id: deliveryId, detailed: true },
      {
        skip: !deliveryId,
      }
    );
  const { data: campaignDeliveryData, isFetching: isFetchingCampaign } =
    useGetProjectByIdQuery(
      { id: campaignDeliveryId, detailed: true },
      {
        skip: !campaignDeliveryId,
      }
    );
  const { data: clients, isFetching: isFetchingClients } =
    useGetAllClientsQuery({});
  const dispatch = useAppDispatch();
  const { editableVersionIndex, deliveryWizardStep, delivery, isDirty } =
    useAppSelector(selectDeliveryWizardData);
  const { tvChannels, movieTheatres, selectDestination } = useDestinations();
  const { createPopup } = usePopup();
  const selectedProductionCompany = useAppSelector(
    selectSelectedProductionCompany
  );
  const selectedPostProductionCompanyId = useAppSelector(
    selectSelectedPostProductionCompanyId
  );

  // short edit allows edit only description, speakers, presentors and content type of version if waiting for post prod/adv ag approval
  const isShortEdit = useMemo(
    () =>
      mode === WizardModeE.Edit &&
      defaultDeliveryData &&
      (defaultDeliveryData.StatusName ===
        StatusE.WaitingForApprovalOfPostProduction ||
        defaultDeliveryData.StatusName ===
          StatusE.WaitingForApprovalOfAdvertisingAgency ||
        defaultDeliveryData.StatusName ===
          StatusE.ApprovedAndSentToBroadcaster ||
        defaultDeliveryData.StatusName === StatusE.DownloadedByBroadcaster ||
        defaultDeliveryData.StatusName ===
          StatusE.DownloadedByAllBroadcasters ||
        user.role === UserRoleE.GlobalAdmin ||
        user.role === UserRoleE.GlobalAdminAndAdminContentManager ||
        // A media-company user edits a delivery only to reassign its campaign association, so they get
        // the same campaign-details-only view as a content manager (no step wizard, no delivery form).
        user.role === UserRoleE.MediaCompany),
    [mode, defaultDeliveryData]
  );

  const shortEditStyle = isShortEdit
    ? {
        pointerEvents: "none",
        filter: "grayscale(100%)",
        opacity: 0.7,
      }
    : undefined;

  useEffect(() => {
    dispatch(setDeliveryWizardStep(1));

    return () => {
      dispatch(clearAll());
      dispatch(setSelectedProductionCompany(null));
    };
  }, []);

  useEffect(() => {
    if (defaultDeliveryData) {
      dispatch(
        setDeliveryData(
          mode === WizardModeE.Create
            ? {
                ...defaultDeliveryData,
                Versions: [{ ...DEFAULT_VERSION_DATA } as any],
                BroadcasterIds: "",
              }
            : defaultDeliveryData
        )
      );
      dispatch(setEditableVersionIndex(mode === WizardModeE.Create ? 0 : -1));
      dispatch(setIsShortEdit(isShortEdit));
    }
  }, [defaultDeliveryData]);

  useEffect(() => {
    if (
      defaultDeliveryData &&
      productionCompaniesSO?.length &&
      !selectedProductionCompany
    ) {
      dispatch(
        setSelectedProductionCompany(
          selectValue(
            productionCompaniesSO,
            defaultDeliveryData?.ProductionCompanyId
          )
        )
      );
    }
  }, [defaultDeliveryData, productionCompaniesSO, selectedProductionCompany]);

  useEffect(() => {
    if (campaignDeliveryData) {
      dispatch(setCampaignDetails(campaignDeliveryData));
      dispatch(
        updateDeliveryData({
          BroadcastingDate: campaignDeliveryData?.BroadcastingDate,
        })
      );
    }
  }, [campaignDeliveryData]);

  useEffect(() => {
    // const popupOverlay = document.getElementById('popup-overlay');
    const popupCloseIcon = document.getElementById("popup-close-icon");

    // popupOverlay.addEventListener('click', overrideHandleClosePopup);
    popupCloseIcon.addEventListener("click", overrideHandleClosePopup);
    return () => {
      // popupOverlay.removeEventListener('click', overrideHandleClosePopup);
      popupCloseIcon.removeEventListener("click", overrideHandleClosePopup);
    };
  }, [isDirty]);

  const overrideHandleClosePopup = (e: MouseEvent) => {
    if (e.target === e.currentTarget && isDirty) {
      e.stopImmediatePropagation();
      createPopup(UnsavedChangesWarning);
    }
  };

  const handleChangeDescription = (value: string) => {
    dispatch(setDeliveryDescription(value));
    dispatch(setIsDirty(true));
  };

  const handleNoneClick = (elementID) => {
    const element: any = document.getElementById(elementID);
    if (element) {
      element.value = t`NONE`;

      if (elementID === "participantNames") {
        // setParticipants('NONE')
        dispatch(updateDeliveryData({ ParticipantNames: t`NONE` }));
      } else {
        // setSpeakerName('NONE')
        dispatch(updateDeliveryData({ SpeakerNames: t`NONE` }));
      }
    }
  };

  const handleChangeParticipants = (value: string) => {
    // setParticipants(value);
    dispatch(updateDeliveryData({ ParticipantNames: value }));
  };
  const handleChangeSpeakerNames = (value: string) => {
    // setParticipants(value);
    dispatch(updateDeliveryData({ SpeakerNames: value }));
  };
  const handleChangeBroadcastingDate = (value: string) => {
    dispatch(updateDeliveryData({ BroadcastingDate: value }));
  };

  const handleChangeProductionLocation = (value: "local" | "abroad") => {
    dispatch(setProductionLocation(value === "local"));
  };

  const createNewProdCompany = () => {
    createPopup(CreateNewProductionCompanyShort() as PopupT);
  };

  const firstStepFormFields: FormFieldT[] =
    isFetchingClients ||
    (mode !== WizardModeE.Create && !defaultDeliveryData) ||
    (mode !== WizardModeE.Create && !selectedProductionCompany) ||
    (deliveryId && isFetchingDelivery) ||
    (campaignDeliveryId && isFetchingCampaign) ||
    !filteredPostProductionCompaniesSO.length ||
    !productionCompaniesSO.length
      ? []
      : [
          {
            label: "Description",
            name: "description",
            validation: ValidatorsE.REQUIRED_STRING_WITH_NO_COMMA_AND_SEMICOLON,
            defaultValue:
              mode !== WizardModeE.Create
                ? delivery?.ProjectComment
                : getDeliveryNameFromCampaignName(campaignDeliveryData?.Name),
            hintText:
              "Here you can write any relevant information for the broadcasters regarding this delivery",
            onChange: handleChangeDescription,
            props: {
              autoFocus:
                user.role !== UserRoleE.GlobalAdminAndAdminContentManager,
            },
            // style: (!isMobileOrTablet() && user.role === UserRoleE.DeliveryService) ? {
            //     gridArea: '1 / 1 / 2 / 3', width: 'calc(50% - 40px)'
            // } : undefined
          },
          {
            label:
              user.role === UserRoleE.Broadcaster ||
              user.role === UserRoleE.DeliveryService
                ? null
                : "Post/Sound production",
            name: "postProductionCompany",
            selectOptions: filteredPostProductionCompaniesSO,
            controlledValue: selectValue(
              filteredPostProductionCompaniesSO,
              +selectedPostProductionCompanyId ||
                defaultDeliveryData?.PostProductionCompanyId
            ),
            defaultValue: selectValue(
              filteredPostProductionCompaniesSO,
              +selectedPostProductionCompanyId ||
                defaultDeliveryData?.PostProductionCompanyId
            ),
            validation: selectedPostProductionCompanyId > 0
              ? null
              : ValidatorsE.SELECT_REQUIRED,
            style: shortEditStyle,
            onChange: ({ value }) =>
              dispatch(setPostProductionCompanyId(value)),
          },
          {
            label:
              user.role === UserRoleE.Broadcaster ? null : "Production company",
            hintText: "Create new production company",
            selectOptions: productionCompaniesSO,
            controlledValue: selectedProductionCompany,
            onChange: (option) => {
              dispatch(setProductionCompanyId(option.value));
              dispatch(setSelectedProductionCompany(option));
            },
            validation: selectedProductionCompany
              ? undefined
              : ValidatorsE.SELECT_REQUIRED,
            // createNewOption: createNewProdCompany,
            // addAction: createNewProdCompany,
          },
          {
            label: "Broadcasting date",
            hintText: "Please select the intended broadcast date",
            type: "date",
            defaultValue: dayjs(delivery?.BroadcastingDate).format(
              "MM/DD/YYYY"
            ),
            onChange: (value) => handleChangeBroadcastingDate(value),
            props: {
              min:
                mode === WizardModeE.Edit &&
                user.role !== UserRoleE.GlobalAdmin &&
                user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
                delivery?.BroadcastingDate
                  ? dayjs(delivery?.BroadcastingDate)
                      .toISOString()
                      .split("T")[0]
                  : mode === WizardModeE.Create &&
                    user.role === UserRoleE.AdvertisingAgency
                  ? dayjs().toISOString().split("T")[0]
                  : undefined,
            },
          },
          {
            label: "Presentors/Actors",
            name: "participantNames",
            defaultValue: delivery?.ParticipantNames,
            validation: delivery?.ParticipantNames
              ? undefined
              : ValidatorsE.REQUIRED_STRING,
            onChange: (v) => handleChangeParticipants(v),
            props: {
              id: "participantNames",
            },
            extraContent: () => (
              <p className={styles.clickNonHint}>
                <Span>If there are no Presenters/Actors, click</Span>{" "}
                <span
                  onClick={() => handleNoneClick("participantNames")}
                  className={styles.noneButton}
                >
                  ‘{t`NONE`}’
                </span>
              </p>
            ),
          },
          {
            label: "Speaker names",
            defaultValue: delivery?.SpeakerNames,
            validation: delivery?.SpeakerNames
              ? undefined
              : ValidatorsE.REQUIRED_STRING,
            onChange: (v) => handleChangeSpeakerNames(v),
            props: {
              id: "speakerNames",
            },
            extraContent: () => (
              <p className={styles.clickNonHint}>
                <Span>If there is no announcer, click</Span>{" "}
                <span
                  onClick={() => handleNoneClick("speakerNames")}
                  className={styles.noneButton}
                >
                  ‘{t`NONE`}’
                </span>
              </p>
            ),
          },
          {
            label: "Production location",
            defaultValue: defaultDeliveryData
              ? defaultDeliveryData.ProductionLocationIsrael
                ? 0
                : 1
              : 0,
            tabOptions: [
              {
                value: "local",
                label: "Local",
              },
              {
                value: "abroad",
                label: "Abroad",
              },
            ],
            onChange: handleChangeProductionLocation,
            style: !isMobileOrTablet() &&
              user.role !== UserRoleE.Broadcaster &&
              user.role !== UserRoleE.DeliveryService && {
                transform: "translateY(-25px)",
              },
          },
        ];

  const submitFirstStep = (data: any) => {
    const body = {
      ProjectComment: data.description,
      PostProductionCompanyId: selectedPostProductionCompanyId || 0,
      ProductionCompanyId: selectedProductionCompany?.value || null,
      ParticipantNames: delivery?.ParticipantNames,
      SpeakerNames: delivery?.SpeakerNames,
      BroadcastingDate: delivery?.BroadcastingDate,
    };

    dispatch(setDeliveryDescription(data.description || ""));
    dispatch(updateDeliveryData(body));
    dispatch(setDeliveryWizardStep(2));
  };

  const handleAddVersion = () => {
    const addEmptyVersionAfterSaving = new CustomEvent(
      "addEmptyVersionAfterSaving"
    );
    if (editableVersionIndex === -1) {
      dispatch(addEmptyVersion());
    } else {
      document.dispatchEvent(addEmptyVersionAfterSaving);
    }
  };

  const handleChangeCampaignNumber = () => {
    createPopup({
      header: <H1>Change campaign</H1>,
      body: (
        <ChangeCampaignNumberBody
          projectId={deliveryId}
          currentCampaignNumber={campaignDeliveryData?.Campaign?.Id}
        />
      ),
      // The buttons depend on the reassignment-target lookup, which only the popup's own children
      // can see — and the popup slice has no updatePopup, so a fixed footer here could never
      // follow the result. Same projectId as the body: a different value would silently fire a
      // second request and leave the footer disagreeing with the body.
      footer: <ChangeCampaignNumberFooter projectId={deliveryId} />,
      className: styles.changeCampaignNumberPopup,
    });
  };

  return (
    <div>
      <WizardHeader
        mode={mode}
        type={type}
        campaignId={defaultDeliveryData?.Campaign?.Id}
        isShortEdit={isShortEdit}
      />
      {isFetchingCampaign && (
        <div className={styles.loaderWrapper}>
          {/* <Span>Loading {isFetchingDelivery ? 'delivery' : 'campaign'} details...</Span> */}
          <Icon
            name="loading"
            size={IconSizeE.S}
            color={IconColorE.LIGHT_GRAY}
          />
        </div>
      )}
      {campaignDeliveryData && (
        <>
          {deliveryWizardStep === 1 && (
            <>
              <div className={styles.campaignDetailsWrapper}>
                <div className={styles.secondHeaderWrapper}>
                  <H2>Campaign details</H2>
                </div>
                <ul className={styles.campaignInfo}>
                  <li>
                    <Span>Campaign ID</Span>: {campaignDeliveryData.Campaign.Id}
                    {mode !== WizardModeE.Create &&
                      user.role !== UserRoleE.Broadcaster &&
                      (user.role === UserRoleE.GlobalAdmin ||
                        user.role ===
                          UserRoleE.GlobalAdminAndAdminContentManager ||
                        user.role === UserRoleE.AdvertisingAgency ||
                        (user.role === UserRoleE.MediaCompany &&
                          user.accessLevel !== AccessLevelE.Guest) ||
                        campaignDeliveryData.CreatedByUserId ===
                          (user as any).userId) && (
                        <Icon
                          name="edit"
                          className={styles.editCampaignNumberIcon}
                          onClick={handleChangeCampaignNumber}
                          title="Change campaign"
                        />
                      )}
                  </li>
                  <li>
                    <Span>Campaign name</Span>:{" "}
                    {campaignDeliveryData.Campaign.Name}
                  </li>
                  <li>
                    <Span>Broadcasting date</Span>:{" "}
                    {dayjs(campaignDeliveryData.BroadcastingDate).format(
                      DATE_FORMAT
                    )}
                  </li>
                  <li>
                    <Span>Advertiser</Span>:{" "}
                    {
                      campaignDeliveryData.Detailed[
                        lang === LangE.EN
                          ? "AdvertiserNameEn"
                          : "AdvertiserName"
                      ]
                    }
                  </li>
                  <li>
                    <Span>Brand</Span>:{" "}
                    {
                      campaignDeliveryData.Detailed[
                        lang === LangE.EN ? "BrandNameEn" : "BrandName"
                      ]
                    }
                  </li>
                  <li>
                    <Span>Market category</Span>:{" "}
                    {campaignDeliveryData.Detailed.IndustryName}
                  </li>
                </ul>
              </div>
              {user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
                user.role !== UserRoleE.MediaCompany && (
                <div className={styles.secondHeaderWrapper}>
                  <H2>Delivery details</H2>
                </div>
              )}
              {!isFetchingDelivery &&
                !isFetchingCampaign &&
                !isFetchingClients &&
                user.role !== UserRoleE.GlobalAdminAndAdminContentManager &&
                user.role !== UserRoleE.MediaCompany &&
                (mode !== WizardModeE.Create
                  ? selectedProductionCompany !== null
                  : true) && (
                  <Form
                    fields={firstStepFormFields}
                    onSubmit={submitFirstStep}
                    formID="delivery-first-step"
                    columns={3}
                  />
                )}
            </>
          )}
          {deliveryWizardStep === 2 && (
            <>
              <div className={styles.secondHeaderWrapper}>
                <H2>Versions</H2>
                <InfoTooltip
                  text="Set the required versions for the campaign. Later on, the process, the post-production company will upload the media files according to those versions."
                  place={TooltipPlaceE.RIGHT}
                />
              </div>
              <div className={styles.versionsHeader}>
                <Span>Version name</Span>
                <Span>Content type</Span>
                <Span>Length (sec)</Span>
                {/* {user.role === UserRoleE.Broadcaster ? <Span>House number</Span> : <span />} */}
              </div>
              {delivery.Versions.map((version, index) => (
                <DeliveryVersionRow
                  key={index}
                  defaultData={version}
                  index={index}
                  isShortEdit={isShortEdit}
                />
              ))}
              {!isShortEdit && (
                <div className={styles.addVersionButtonWrapper}>
                  <Button
                    icon="addCircleFilled"
                    mode={ButtonModeE.LINK}
                    onClick={handleAddVersion}
                  >
                    Add version
                  </Button>
                </div>
              )}
            </>
          )}
          {deliveryWizardStep === 3 && (
            <>
              <div className={styles.secondHeaderWrapper}>
                <H2>Destinations</H2>
              </div>
              <div className={styles.destinationsWrapper}>
                <H4>TV channels</H4>
                <ul>
                  {tvChannels.map((broadcaster, index) => (
                    <Tooltip key={index} text={broadcaster.Name} delayShow={0}>
                      <li
                        className={
                          delivery?.BroadcasterIds?.split(",")
                            ?.map((item) => +item)
                            ?.includes(broadcaster.Id)
                            ? styles.selectedDestination
                            : ""
                        }
                        onClick={() => selectDestination(broadcaster.Id)}
                      >
                        <AsyncImage
                          link={getImageLink({ id: broadcaster.Id })}
                        />
                      </li>
                    </Tooltip>
                  ))}
                </ul>
                {user.role !== UserRoleE.Broadcaster && <H4>Movie theaters</H4>}
                <ul>
                  <ul>
                    {movieTheatres.map((broadcaster, index) => (
                      <Tooltip
                        key={index}
                        text={broadcaster.Name}
                        delayShow={0}
                      >
                        <li
                          className={
                            delivery?.BroadcasterIds?.split(",")
                              ?.map((item) => +item)
                              ?.includes(broadcaster.Id)
                              ? styles.selectedDestination
                              : ""
                          }
                          onClick={() => selectDestination(broadcaster.Id)}
                        >
                          <AsyncImage
                            link={getImageLink({ id: broadcaster.Id })}
                          />
                        </li>
                      </Tooltip>
                    ))}
                  </ul>
                </ul>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default DeliveryWizard;
